import React, { useState } from "react";
import { connect } from "react-redux";
import {
  getUserRoleId,
  getClientid,
  getRoleList,
  getUserRole,
  getMenuList,
} from "redux/selectors";
import styled from "@emotion/styled";
import CheckInCheckout from "molecules/CheckInCheckout";
import UserMonthlyReport from "molecules/UserMonthlyReport";
import UserCheckinReport from "molecules/UserCheckinReport";
import NonStaffDashboard from "molecules/NonStaffDashboard";
import UserLeaveReport from "molecules/UserLeaveReport";
import DashboardMatrix from "./DashboardComponents/DashboardMatrix";
import TimesheetData from "./DashboardComponents/TimesheetData";
import IndividualStat from "./DashboardComponents/IndividualStat";
import EngagementCalendar from "./DashboardComponents/EngagementCalendar";
import RecentJoiners from "./DashboardComponents/RecentJoiners";
import MyCV from "./DashboardComponents/MyCV";
import DocumentTableView from "./DocumentFolders/DocumentTableView";
import AnnouncementList from "./DashboardComponents/AnnouncementList";
import BirthdaySpotLight from "./DashboardComponents/BirthdaySpotLight";
import { Col, Row } from "antd";
import MyNotifications from "./DashboardComponents/MyNotifications";
import usePendingCounts from "./DashboardComponents/usePendingCounts";
import dayjs from "dayjs";
import moment from "moment";
import { MAIN_CLIENT_ID } from "common/Constants";

// ---------------------------------------------------------------------
// PERMISSION FIX: the "Dashboard -> Mine" menu entry (key "dashboard")
// carries { view, create, edit, delete }. Resolve it once here and hand it
// to the widgets so Add / Edit / Delete controls respect it.
// ---------------------------------------------------------------------
const DASHBOARD_MENU_KEY = "dashboard";

const resolveDashboardPermission = (menuList, userRoleId) => {
  const rawList =
    menuList?.[userRoleId] || Object.values(menuList || {})?.[0] || [];
  const list = Array.isArray(rawList) ? rawList : [];
  const menu = list.find((item) => item?.key === DASHBOARD_MENU_KEY);

  // No entry or no access -> everything denied
  if (!menu || !menu.access) {
    return { view: false, create: false, edit: false, delete: false };
  }

  return {
    view: menu.permission?.view === true,
    create: menu.permission?.create === true,
    edit: menu.permission?.edit === true,
    delete: menu.permission?.delete === true,
  };
};

const StyledChartContainer = styled.div`
  padding: 15px;
  box-shadow: 0px 4px 30px 0px #0000001f;
  border-radius: 8px;
`;

const StyledChartContainerFill = styled(StyledChartContainer)`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const GridContainer = styled.div`
  display: grid;
  /* grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); */
  /* grid-template-columns: repeat(2, 1fr); */
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  padding-top: 5px;
  grid-template-areas:
    "check-in-monthly user-checkin-report"
    "user-leave-report user-leave-report"; // Assign areas

  @media (max-width: 1024px) {
    // Adjust for medium screens
    grid-template-columns: repeat(auto-fill, 1fr);
    grid-template-areas:
      "check-in-monthly"
      "user-checkin-report"
      "user-leave-report";
  }
`;

const GridItem = styled.div`
  min-height: 200px;
  display: flex;
  flex-direction: column;
  &.check-in-monthly {
    grid-area: check-in-monthly;
  }
  &.user-checkin-report {
    grid-area: user-checkin-report;
  }
  &.user-leave-report {
    grid-area: user-leave-report;
  }
`;

const MyDashboard = ({ userRoleId, ClientId, menuList }) => {
  const [selectMonthYear, setSelectMonthYear] = useState({
    month: dayjs().format("MMMM"),
    year: dayjs().year(),
  });
  const [engagementsData, setEngagementsData] = useState("");
  const { pendingCounts, lastUpdated, isLoading: notificationsLoading } = usePendingCounts();

  const dashboardPermission = resolveDashboardPermission(menuList, userRoleId);

  const format =
    selectMonthYear.year && selectMonthYear.month
      ? "MMMM YYYY"
      : selectMonthYear.year
        ? "YYYY"
        : selectMonthYear.month
          ? "MMMM"
          : null;

  const formattedDate = format
    ? moment(
      `${selectMonthYear.month} ${selectMonthYear.year}`,
      "MMMM YYYY"
    ).format(format)
    : "";

  return (
    <>
      <MyDashboardWrap>
        <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
          <Col xs={24} sm={24} md={24} lg={17}>
            {ClientId !== MAIN_CLIENT_ID && <CheckInCheckout />}

            <StyledChartContainer>
              <EngagementCalendar
                selectMonthYear={selectMonthYear}
                setSelectMonthYear={setSelectMonthYear}
                engagementsData={engagementsData}
                setEngagementsData={setEngagementsData}
              />
            </StyledChartContainer>
          </Col>
          <Col xs={24} sm={24} md={24} lg={7}>
            <MyNotifications pendingCounts={pendingCounts} lastUpdated={lastUpdated} isLoading={notificationsLoading} />
          </Col>
        </Row>
        <GridContainer>

          <GridItem className="check-in-monthly">
            <StyledChartContainerFill>
              <TimesheetData permission={dashboardPermission} />
            </StyledChartContainerFill>
          </GridItem>
          <GridItem className="user-checkin-report">
            <StyledChartContainerFill>
              <AnnouncementList />
            </StyledChartContainerFill>
          </GridItem>

        </GridContainer>
        <GridContainer>

          <GridItem className="check-in-monthly">
            <StyledChartContainerFill>
              <RecentJoiners />
            </StyledChartContainerFill>
          </GridItem>
          <GridItem className="user-checkin-report">
            <StyledChartContainerFill>
              <BirthdaySpotLight />
            </StyledChartContainerFill>
          </GridItem>

        </GridContainer>
        <GridContainer>
          <GridItem className="check-in-monthly">
            <StyledChartContainer>
              <DashboardMatrix />
            </StyledChartContainer>
          </GridItem>
          <GridItem className="user-checkin-report">
            <StyledChartContainer>
              <MyCV permission={dashboardPermission} />
            </StyledChartContainer>
          </GridItem>

        </GridContainer>

        <GridContainer>

          <GridItem className="check-in-monthly">
            <UserMonthlyReport />
          </GridItem>
          <GridItem className="user-checkin-report">
            <UserCheckinReport />
          </GridItem>
          <GridItem className="user-leave-report">
            <UserLeaveReport />
          </GridItem>
        </GridContainer>
        {userRoleId == 1 && <NonStaffDashboard />}
      </MyDashboardWrap>
    </>
  );
};

const mapStateToProps = (state) => ({
  userRoleId: getUserRoleId(state),
  ClientId: getClientid(state),
  roleList: getRoleList(state),
  userRoleType: getUserRole(state),
  menuList: getMenuList(state),
});

export default connect(mapStateToProps, null)(MyDashboard);

const MyDashboardWrap = styled.div``;